import React, { ErrorInfo, ReactNode } from 'react';

declare var Sentry: any;

interface ErrorBoundaryProps {
  children?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  eventId: string | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = {
    hasError: false,
    eventId: null,
  };

  static getDerivedStateFromError(_: Error): Partial<ErrorBoundaryState> {
    // Update state so the next render will show the fallback UI.
    return { hasError: true };
  }

  // Fix: Converted to an arrow function property to explicitly bind `this` and ensure `this.setState` is available.
  componentDidCatch = (error: Error, errorInfo: ErrorInfo) => {
    console.error("Uncaught error:", error, errorInfo);
    if (typeof Sentry !== 'undefined' && Sentry.captureException) {
        const eventId = Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
        this.setState({ eventId });
    }
  }
  
  handleFeedback = () => {
      if (this.state.eventId && typeof Sentry !== 'undefined' && Sentry.showReportDialog) {
          Sentry.showReportDialog({ eventId: this.state.eventId });
      }
  }

  // Fix: Converted to an arrow function property to explicitly bind `this` and ensure `this.props` is available.
  render = () => {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-[#0F172A] text-white flex flex-col items-center justify-center p-4 text-center">
            <div className="text-5xl mb-4">⚙️</div>
            <h1 className="text-3xl font-bold text-red-400 mb-2">Application Error</h1>
            <p className="text-gray-400 max-w-md mb-6">
                We're sorry, but something went wrong. Our team has been automatically notified of this issue.
            </p>
            <div className="flex gap-4">
                 <button 
                    onClick={() => window.location.reload()}
                    className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2 px-6 rounded-lg transition-colors"
                 >
                    Reload Page
                </button>
                 <button 
                    onClick={this.handleFeedback}
                    className="bg-gray-700 hover:bg-gray-600 text-white font-semibold py-2 px-6 rounded-lg transition-colors"
                 >
                    Provide Feedback
                </button>
            </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
